import { useSkipLink } from '../../providers/SkipLinkProvider';
import './NDCapis.scss';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { visuallyHidden } from '@mui/utils';
import Button from '@mui/material/Button';


interface GettingStartedOverviewProps {
  handleClick: (value: string[]) => void;
  data?: any[];
  isFooterNavigation?: boolean;
  setApiVersionNew: () => void;
  apiVersionNew?: boolean,
}

export default function GettingStartedOverview({
  handleClick,
  data,
  isFooterNavigation,
  setApiVersionNew,
  apiVersionNew
}: GettingStartedOverviewProps) {
  const { setNavigationChange } = useSkipLink();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [localApiVersion, setLocalApiVersion] = useState(apiVersionNew ?? false);

  useEffect(() => {
    // Add a small delay to ensure the component is fully mounted and visible
    const timer = setTimeout(() => {
      if (isFooterNavigation && headingRef.current) {
        headingRef.current.setAttribute('tabindex', '-1'); // Use -1, not 1
        headingRef.current.focus();
      }
    }, 220); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
    // eslint-disable-next-line 
  }, []); // Empty dependency array - run only once when component mounts

  const getData = (json, matchOn, key, returnFirst = false) => {
    if (data) {
      let result = json.filter(
        (x) => x.link[key] === matchOn && x.link.enabled
      );
      if (returnFirst) {
        result = result[0].link;
      }
      return result;
    }
  };

  const { t, i18n } = useTranslation();

  //const apiNavData = getData(data["navigation"], "api_info", "menu_name");

  const apiNavData = localApiVersion ? Object.values(
    t('navigation', { returnObjects: true })
  ).filter((x) => x.link.menu_name === 'api_info_25' && x.link.enabled) : Object.values(
    t('navigation', { returnObjects: true })
  ).filter((x) => x.link.menu_name === 'api_info' && x.link.enabled);

  const apiDocTitle = Object.values(
    t('navigation', { returnObjects: true })
  ).find(
    item => item.link.route === "documentation"
  )?.link.title;

  const gettingStartedPgMenu = localApiVersion ? Object.values(
    t('navigation', { returnObjects: true })
  ).filter((x) => x.link.menu_name === 'ndcapis_25_pg' && x.link.enabled) : Object.values(
    t('navigation', { returnObjects: true })
  ).filter((x) => x.link.menu_name === 'ndcapis_pg' && x.link.enabled);

  //const gettingStartedPgMenu = getData(
  //   data["navigation"],
  //   "ndcapis_pg",
  //   "menu_name"
  // );

  //BELOW IS GETTING THE HIGH LEVEL Getting Started PAGE navigation info
  const getstartmenu = gettingStartedPgMenu[0]?.link.subtree;

  const getStartSection = getData(getstartmenu, 'letsstart', 'route', true);
  const getAPIsection = getData(getstartmenu, 'allservices', 'route', true);

  //BELOW IS GETTING the menu items for each section (I.e. what is used in left hand nav) to iterate through
  const APIsNavitems = getData(apiNavData, 'documentation', 'route');
  //  const rlsNotesNavitems = apiNavData?.find((item) => item.link.route === "releasenotes");

  const visibleNavItems = APIsNavitems?.[0]?.link?.subtree?.filter((x) => x.link.show_on_gs_pg_nav) ?? [];

  const checkRoute = (x) => {
    let route = ['documentation', x.route];
    if (x.default_children) {
      route.push('functionality');
    }
    handleClick(route);
  };

  const getAriaLabel = (language: string, title: string) => {
    const labels: { [key: string]: string } = {
      fr: `page de ${title}`,
      en: `${title} page`
    };
    return labels[language] || `Link to ${title} page`;
  };

  return (
    <>
      <div className="ndcAPIsContainer">
        <div className="backgroundImg">
          <div className="overviewBGoverlay"></div>
          <h1 className="title" ref={headingRef}>
            {getStartSection.title}
          </h1>
        </div>
        <div className="webServices">
          <h2 className="webServicesTitle">{getAPIsection.title}</h2>

          <p className="webServicesSubTitle">{getAPIsection.subtitle}</p>
          {getAPIsection.apiVersionSwitch && (
            <Button
              variant="outlined"
              onClick={() => { setLocalApiVersion(prev => !prev); setApiVersionNew(); }}
              aria-label={t(getAPIsection.apiVersionSwitch.buttonLabel, {
                currentVersion: !localApiVersion ? getAPIsection.apiVersionSwitch.version_17_2 : getAPIsection.apiVersionSwitch.version_25_1,
                nextVersion: !localApiVersion ? getAPIsection.apiVersionSwitch.version_25_1 : getAPIsection.apiVersionSwitch.version_17_2,
              }) as string}
              aria-pressed={undefined}
              endIcon={
                <img
                  src="/images/swap-arrows.svg"
                  alt=""
                  style={{ width: 16, height: 16, display: 'block' }}
                />
              }
              sx={{
                minWidth: 120,
                width: { xs: '100%', sm: 'fit-content' },
                // height: 30,
                gap: '6px',
                paddingTop: '5px',
                paddingBottom: '5px',
                paddingLeft: '16px',
                paddingRight: '16px',
                borderRadius: '16px',
                border: '1px solid #1460AA',
                color: '#1460AA',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                // ── Typography ──────────────────────────
                fontFamily: "'AC Nord Text', sans-serif",
                fontWeight: 700,
                fontStyle: 'normal',           // Bold is expressed via fontWeight
                fontSize: '14px',              // labels/md
                lineHeight: '20px',            // labels/md
                letterSpacing: '0px',
                textAlign: 'center',
                verticalAlign: 'middle',
                marginBottom: '30px',
                // ────────────────────────────────────────
                '&:hover': {
                  borderColor: '#1460AA',
                  backgroundColor: 'rgba(20, 96, 170, 0.05)',
                },
                '&:focus-visible': {
                  outline: '3px solid #1460AA',
                  outlineOffset: '2px',
                },
              }}
            >
              {t(getAPIsection.apiVersionSwitch.buttonText, { version: !localApiVersion ? getAPIsection.apiVersionSwitch.version_25_1 : getAPIsection.apiVersionSwitch.version_17_2 })}
            </Button>
          )}
          <nav aria-label={`${apiDocTitle}`}>
            <ul className="linksContainer">
              {visibleNavItems.map((x, i) => (
                    <li key={`${x.link.route}-li`}>
                      <div
                        role="link"
                        tabIndex={0}
                        id={`link-${i}`}
                        key={`${x.link.route}-lc`}
                        onClick={() => {
                          checkRoute(x.link);
                          setNavigationChange(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            checkRoute(x.link);
                            setNavigationChange(true);
                          }
                        }}
                        className="link"
                        aria-label={getAriaLabel(i18n.language, x.link.title)}
                        aria-labelledby={`link-${i} sr-${i}`}
                      >
                        <span style={visuallyHidden} id={`sr-${i}`}>{i + 1} of {visibleNavItems.length}</span>
                        <div className="icon">
                          <img
                            src={`/images/icons/${x.link.title === 'OrderView'
                              ? 'orderretrieve'
                              : x.link.route
                              }.png`} //orderview img icon corrupted, can't get it to replace (will only work locally if try)...
                            alt={`${x.link.title === 'OrderRetrieve'
                              ? 'OrderView'
                              : x.link.title
                              } icon`}
                          ></img>
                        </div>
                        <div className="lable" style={{ wordBreak: "keep-all" }}>
                          <p>{x.link.title}</p>
                        </div>
                      </div>
                    </li>
              ))}
            </ul>
          </nav>

        </div>
      </div>
    </>
  );
}
